import { useEffect, useState } from "react";
import styles from "./Chatbot.module.css";
import { MessageSquare, Send, Maximize2, Minimize2 } from "lucide-react";
import ChatService from "../../services/chat-service";

export default function Chatbot({ welcomeMessage, companyName = "Softindex" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExtended, setIsExtended] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
      const eased = Math.max(0, Math.round(keyboardHeight - 40));
      const offset = Math.min(eased, 120);
      document.documentElement.style.setProperty(
        "--chat-keyboard-offset",
        `${offset}px`
      );
      document.documentElement.style.setProperty(
        "--chat-visual-viewport-height",
        `${Math.round(vv.height)}px`
      );
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("focusin", update);
    window.addEventListener("focusout", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("focusin", update);
      window.removeEventListener("focusout", update);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const mq = window.matchMedia?.("(max-width: 768px)");
    if (!mq?.matches) return;

    const prevOverflow = document.body.style.overflow;
    const prevOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.overscrollBehavior = prevOverscroll;
    };
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setIsExtended(false);
    }
  };

  const toggleExtended = () => {
    setIsExtended(!isExtended);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      const userMessage = input.trim();
      const newUserMessage = {
        id: Date.now(),
        text: userMessage,
        sender: "user",
      };

      setMessages((prevMessages) => [...prevMessages, newUserMessage]);
      setInput("");
      setIsLoading(true);

      const thinkingMessage = {
        id: Date.now() + 0.5,
        text: "Thinking...",
        sender: "ai",
        isThinking: true,
      };
      setMessages((prevMessages) => [...prevMessages, thinkingMessage]);

      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const response = await ChatService.sendMessage(userMessage);

        setMessages((prevMessages) => {
          const withoutThinking = prevMessages.filter((msg) => !msg.isThinking);

          const aiMessage = {
            id: Date.now() + 1,
            text:
              response.reply ||
              "I'm sorry, I couldn't process your request right now.",
            sender: "ai",
          };

          return [...withoutThinking, aiMessage];
        });
      } catch (error) {
        console.error("Error getting AI response:", error);

        setMessages((prevMessages) => {
          const withoutThinking = prevMessages.filter((msg) => !msg.isThinking);
          const errorMessage = {
            id: Date.now() + 1,
            text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
            sender: "ai",
          };

          return [...withoutThinking, errorMessage];
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <button
        className={`${styles.chatIcon} ${isOpen ? styles.chatIconHidden : ""}`}
        onClick={toggleChat}
        aria-label="Open chat"
      >
        <MessageSquare size={28} />
      </button>

      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={toggleChat} />
          <div
            className={`${styles.chatModal} ${isExtended ? styles.extended : ""}`}
          >
          <div className={styles.chatHeader}>
            <h3>Chat with us</h3>
            <div className={styles.headerButtons}>
              <button
                onClick={toggleExtended}
                className={styles.extendButton}
                aria-label={isExtended ? "Minimize chat" : "Extend chat"}
              >
                {isExtended ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
              <button
                onClick={toggleChat}
                className={styles.closeButton}
                aria-label="Close chat"
              >
                &times;
              </button>
            </div>
          </div>
          <div className={styles.chatMessages}>
            {messages.length === 0 && (
              <div className={styles.welcomeMessage}>
                {welcomeMessage ||
                  `Welcome! Ask me anything about ${companyName}.`}
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`${styles.message} ${styles[msg.sender]} ${
                  msg.isThinking ? styles.thinking : ""
                }`}
              >
                {msg.isThinking ? (
                  <span className={styles.thinkingText}>
                    Thinking
                    <span className={styles.dots}>
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </span>
                ) : (
                  msg.text
                )}
              </div>
            ))}
          </div>
          <form onSubmit={handleSendMessage} className={styles.chatInputForm}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className={styles.chatInput}
              disabled={isLoading}
              onFocus={() => {
                const vv = window.visualViewport;
                if (!vv) return;
                const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
                const offset = Math.min(Math.max(0, Math.round(keyboardHeight - 40)), 120);
                document.documentElement.style.setProperty(
                  "--chat-keyboard-offset",
                  `${offset}px`
                );
                document.documentElement.style.setProperty(
                  "--chat-visual-viewport-height",
                  `${Math.round(vv.height)}px`
                );
              }}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={isLoading}
              aria-label="Send message"
            >
              <Send size={20} />
            </button>
          </form>
          </div>
        </>
      )}
    </>
  );
}
